import { useCallback, useEffect } from "react";
import { ProviderUser, CreateProviderUser, UpdateProviderUser } from "../types/models/ProviderUser.d";
import { UpdateEntityProviderUser } from "../types/models/ProviderUser";
import { useGetProviderUserListQuery, useInsertProviderUserMutation, useUpdateProviderUserMutation, useDeleteProviderUserMutation, } from "../apis/ProviderUser.api";
import { useDeleteDialog, useDescriptionAlert } from '../../share';
import { useNavigate } from "react-router";
import { t } from "@lingui/core/macro";

export default function useProviderUser(page: number, pageSize: number) {

    const navigate = useNavigate()
    const { openDialog, closeDialog } = useDeleteDialog();

    const { openSuccessAlert, openFailAlert } = useDescriptionAlert()

    const [insertProviderUserMutation, { 
        isLoading: insertProviderUserLoading, 
        isSuccess: insertProviderUserIsSuccess,
        isError: insertProviderUserIsError,
        error: insertProviderUserError,
    }] = useInsertProviderUserMutation()
    const [updateProviderUserMutation, { 
        isLoading: updateProviderUserLoading,
        isSuccess: updateProviderUserIsSuccess,
        isError: updateProviderUserIsError,
        error: updateProviderUserError,
    }] = useUpdateProviderUserMutation()
    const [deleteProviderUserMutation, { 
        isLoading: deleteProviderUserLoading,
        isSuccess: deleteProviderUserIsSuccess,
        isError: deleteProviderUserIsError,
        error: deleteProviderUserError,
    }] = useDeleteProviderUserMutation()

    const loading =
        insertProviderUserLoading ||
        updateProviderUserLoading ||
        deleteProviderUserLoading ||
        false

    const insertProviderUser = useCallback(async (providerUser: CreateProviderUser, redirectPath: string|undefined) => {
        try {
            const data = await insertProviderUserMutation(providerUser).unwrap()
            if (data) {
                if (redirectPath) {
                    if (redirectPath !== '.') {
                        navigate(redirectPath, {relative: 'path'})
                    }
                } else {
                    navigate(`../${data.id}`, {relative: 'path'})
                }
                return data;
            }
        } catch (e) {
            console.log("add error", e)
            openFailAlert(t`Add error: ` + e.toString())
        }
    }, [insertProviderUserMutation, navigate])
    
    useEffect(() => {
        if (insertProviderUserIsSuccess) {
            openSuccessAlert('Provider User created')
        } else if (insertProviderUserIsError) {
            console.log("add error", insertProviderUserError)
            openFailAlert(t`Provider User add error: ` + insertProviderUserError?.data?.message)
        }
    }, [
        insertProviderUserIsSuccess,
        insertProviderUserIsError,
        insertProviderUserError
    ]);

    const updateProviderUser = useCallback(async (providerUser: UpdateEntityProviderUser, redirectPath: string|undefined) => {
        try {
            const data = await updateProviderUserMutation(providerUser).unwrap();
            if (data != null) {
                //if update success
                if (redirectPath) {
                    if (redirectPath !== '.') {
                        navigate(redirectPath, {relative: 'path'})
                    }
                } else {
                    navigate('..', {relative: 'path'})
                }
                return data;
            }
        } catch (e) {
            console.log("update error", e)
            openFailAlert(t`Update error: ` + e.toString())
        }
    }, [updateProviderUserMutation, navigate])
    
    useEffect(() => {
        if (updateProviderUserIsSuccess) {
            openSuccessAlert(t`Provider User updated`)
        } else if (updateProviderUserIsError) {
            console.log("update error", updateProviderUserError)
            openFailAlert(t`Provider User update error: ` + updateProviderUserError?.data?.message)
        }
    }, [
        updateProviderUserIsSuccess,
        updateProviderUserIsError,
        updateProviderUserError
    ]);

    const deleteProviderUser = useCallback((providerUser: ProviderUser, redirectPath: string|undefined) => {
        openDialog(async () => {
            providerUser = {
                ...providerUser,
            }
            try {
                const { data } = await deleteProviderUserMutation(providerUser);
                if (data != null) {
                    if (redirectPath) {
                        navigate(redirectPath, {relative: 'path'})
                    }
                }
            } catch (e) {
                console.log("delete error", e)
                openFailAlert(t`Delete error: ` + e.toString())
            }
            closeDialog()
        });
    }, [deleteProviderUserMutation, closeDialog, openDialog])
    
    useEffect(() => {
        if (deleteProviderUserIsSuccess) {
            openSuccessAlert(t`Provider User deleted`)
        } else if (deleteProviderUserIsError) {
            console.log("delete error", deleteProviderUserError)
            openFailAlert(t`Provider User delete error: ` + deleteProviderUserError?.data?.message)
        }
    }, [
        deleteProviderUserIsSuccess,
        deleteProviderUserIsError,
        deleteProviderUserError
    ]);

    return {
        loading,
        insertProviderUser,
        updateProviderUser,
        deleteProviderUser
    }
}
